<script setup lang="ts">
import { Check, Copy, Files, LoaderCircle, RefreshCw, Star, Trash2, TriangleAlert } from '@lucide/vue'
import {
    Button,
    type ButtonVariants,
    Checkbox,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    ZhEmptyState,
    ZhKeyValue,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhStatusPill,
    ZhSystemLabel,
} from '@zerohuman/ui'
import { type Component, computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import {
    api,
    asListResponse,
    type CatalogTask,
    CRAFTED_NOT_IN_PLAN_MESSAGE,
    isCraftedRefusal,
    type ListQuery,
    type Role,
    type TaskCopyInput,
    type TaskRow,
    type Team,
} from '@/api'
import CatalogAdoptionPanel from '@/components/CatalogAdoptionPanel.vue'
import DataTable, { type DataTableColumn } from '@/components/DataTable.vue'
import ListSearchField from '@/components/ListSearchField.vue'
import TaskCopyForm from '@/components/TaskCopyForm.vue'
import { useListState } from '@/composables/useListState'
import { useUiMode } from '@/composables/useUiMode'
import {
    ALL_FUNCTIONS,
    BUSINESS_FUNCTION_OPTIONS,
    catalogFilterQuery,
    firstSentence,
    taskDisplayName,
} from '@/lib/catalogText'
import { buildGateBadges } from '@/lib/gateBadges'
import { cloneOnLabel, entryOwnerVersion, ownerVersionLabel } from '@/lib/ownerVersion'
import { groupToolsByVendor } from '@/lib/toolChips'

/*
 * Every control, surface, chip and empty state here is the kit's (#1226): every picker is the kit `Select`, the
 * clone cards and the table sit on kit surfaces, the scope chips are kit chips, and each row action is a kit `Button`
 * whose look is a kit variant named for its state. The screen states no colour, radius, border or fill of its own.
 * DataTable is portal-wide and migrates under its own item; here it only sits on the surface.
 */

const route = useRoute()
/** Teams by default; a `?kind=` link, or the kind saved from the last visit (#633), opens on another. */
const kind = ref<'team' | 'role' | 'task'>('team')
const searchDraft = ref('')
const catalogKinds = [
    { value: 'team', label: 'Teams', hint: 'A complete team, ready to make your own.' },
    { value: 'role', label: 'Roles', hint: 'Add a teammate and their tasks to an existing team.' },
    { value: 'task', label: 'Tasks', hint: 'Find individual tasks to clone or copy.' },
] as const
const currentKind = computed(() => catalogKinds.find(item => item.value === kind.value) ?? catalogKinds[0])
function submitSearch() {
    page.value = 1
    search.value = searchDraft.value.trim()
}

/**
 * The business-function and needs-no-tools filters (#1727). Held here, as the search is, so they carry over between
 * Teams, Roles and Tasks; the server narrows each list by them. Changing either starts the list again from page 1.
 */
const businessFunction = ref<string>(ALL_FUNCTIONS)
const needsNoTools = ref(false)
const filterQuery = computed(() => catalogFilterQuery(businessFunction.value, needsNoTools.value))

function chooseFunction(choice: unknown): void {
    businessFunction.value = choice === null || choice === undefined ? ALL_FUNCTIONS : String(choice)
    page.value = 1
}

function chooseNeedsNoTools(checked: boolean | 'indeterminate'): void {
    needsNoTools.value = checked === true
    page.value = 1
}

/**
 * Standard shows no slug and no exact tool name anywhere on the screen; Advanced shows both, and keeps the Scopes
 * column (#1727, PRD #1697 rule 6).
 */
const { mode } = useUiMode()
const advanced = computed(() => mode.value === 'advanced')

/** The entry whose Details sheet is open: its full goal, tools, approvals, provenance and version. */
const detailsTask = ref<CatalogTask | null>(null)

function closeDetails(open: boolean): void {
    if (!open) detailsTask.value = null
}

/**
 * How a message, or a clone's version line, names a task: by its name. For an entry published with no name, Standard
 * reads "Unnamed task", as the row does, since Standard shows no slug anywhere on the screen (#1727); Advanced, which
 * shows slugs, names it by its slug.
 */
function messageName(task: CatalogTask): string {
    const named = task.name?.trim()
    if (named) return named
    return advanced.value ? task.slug : taskDisplayName(task)
}

const tasks = ref<CatalogTask[]>([])
const teams = ref<Team[]>([])
const roles = ref<Role[]>([])
const error = ref<string | null>(null)
/** The page-level message is the plan's refusal of Zero Human content (#449), so it links to Billing. */
const planRefused = computed(() => error.value === CRAFTED_NOT_IN_PLAN_MESSAGE)
const busy = ref<string | null>(null)
/**
 * The one clone in flight, if any. This is the one-clone-at-a-time guard, kept apart from the
 * per-row display state (#1139): a click on another row while it is set does nothing. It clears
 * when the request settles, not when the result stops showing.
 */
const cloning = ref<string | null>(null)
const copySource = ref<CatalogTask | null>(null)
const copyBusy = ref(false)
const router = useRouter()

function copied(task: TaskRow): void {
    copySource.value = null
    void router.push(`/tasks/${task.id}`)
}

function createCopy(body: TaskCopyInput): Promise<TaskRow> {
    if (!copySource.value) return Promise.reject(new Error('task_not_found'))
    return api.copyCatalogTask(copySource.value.id, body)
}

/** The catalog grows with every published task, so the server hands out one page at a time. */
const total = ref(0)
const page = ref(1)
const limit = ref(25)
const pages = ref(1)
const search = ref('')
const requestedCopy = ref(typeof route.query['copy'] === 'string' ? route.query['copy'] : '')
// By name: the list shows names, so one sorted by slug would read as unsorted (#1727).
const sort = ref('name')
const order = ref<'ASC' | 'DESC'>('ASC')
const filters = ref<Record<string, string>>({})
const loading = ref(false)
/**
 * #633: the kind, the submitted search, the sort, the filters and the business-function and needs-no-tools choices
 * come back on the next visit, in place before the first read. The page number is not kept. A `?kind=` or
 * `?search=` link wins for its visit. The kind is which list this is, so Clear filters leaves it.
 */
const list = useListState('catalog', {
    kind: { ref: kind, query: 'kind', allowed: () => catalogKinds.map(item => item.value), keep: true },
    search: { ref: search, query: 'search' },
    sort: { ref: sort },
    order: { ref: order },
    filters: { ref: filters },
    businessFunction: {
        ref: businessFunction,
        allowed: () => BUSINESS_FUNCTION_OPTIONS.map(option => option.value),
    },
    needsNoTools: { ref: needsNoTools },
})
searchDraft.value = search.value

function clearFilters(): void {
    list.clear()
    page.value = 1
}

onMounted(async () => {
    try {
        teams.value = await api.teams()
        roles.value = await api.roles()
    } catch {
        error.value = 'Could not load catalog.'
    }
})

/** The page, search, sort and filters the table is showing. */
function listQuery(): ListQuery {
    return {
        page: page.value,
        limit: limit.value,
        search: search.value,
        sort: sort.value,
        order: order.value,
        ...filters.value,
        ...filterQuery.value,
    }
}

let taskLoadSequence = 0
async function load(): Promise<void> {
    if (kind.value !== 'task') return
    const sequence = ++taskLoadSequence
    const query = listQuery()
    loading.value = true
    try {
        const listed = asListResponse(await api.catalogTasks(query), query)
        if (sequence !== taskLoadSequence) return
        tasks.value = listed.data
        if (requestedCopy.value) {
            const requested = listed.data.find(task => task.id === requestedCopy.value)
            if (requested) {
                copySource.value = requested
                requestedCopy.value = ''
            }
        }
        total.value = listed.total
        // The page stays the one asked for: writing the answer's page back would ask again for a page nobody chose.
        limit.value = listed.limit
        pages.value = listed.pages
        error.value = null
    } catch {
        if (sequence !== taskLoadSequence) return
        error.value = 'Could not load catalog.'
    } finally {
        if (sequence === taskLoadSequence) loading.value = false
    }
}

watch([kind, page, limit, search, sort, order, filters, filterQuery], load, { immediate: true })
watch(search, value => {
    searchDraft.value = value
})
async function adopted() {
    teams.value = await api.teams()
    roles.value = await api.roles()
    await load()
}
/** A team created from the role or team clone's team choice: read again, so the other clone panel offers it too. */
async function teamCreated() {
    try {
        teams.value = await api.teams()
    } catch {
        // The panel already lists the team it created; the next read brings it here.
    }
}

const allColumns: DataTableColumn[] = [
    {
        // A task is known by its name (#1727); Advanced adds the slug under it.
        key: 'name',
        label: 'Task',
        sort: 'name',
        defaultVisible: true,
    },
    {
        key: 'currentVersion.goal',
        label: 'Goal',
        filterable: false,
        defaultVisible: true,
    },
    {
        // Declared tool types and gate classes, read before Clone rather than after it (#410).
        key: 'scopes',
        label: 'Scopes',
        filterable: false,
        defaultVisible: true,
    },
    {
        key: 'provenance',
        label: 'Provenance',
        type: 'select',
        filterable: true,
        options: [
            { value: 'zerohuman-crafted', label: 'Zero Human' },
            { value: 'community', label: 'community' },
        ],
        defaultVisible: true,
    },
    {
        // The catalog only ever lists public definitions, so there is nothing to filter on here.
        key: 'visibility',
        label: 'Visibility',
        filterable: false,
        defaultVisible: false,
    },
    {
        key: 'status',
        label: 'Status',
        type: 'select',
        filterable: true,
        options: [
            { value: 'active', label: 'Active' },
            { value: 'deprecated', label: 'Deprecated' },
        ],
        defaultVisible: true,
    },
    {
        key: 'actions',
        label: 'Actions',
        filterable: false,
        defaultVisible: true,
        class: 'text-right',
    },
]

/**
 * Standard leaves the Scopes column out: its chips name tools, which Standard never shows (#1727). What a task
 * reaches for stays one click away in its Details sheet, by vendor. Advanced keeps the column as #410 drew it.
 */
const columns = computed(() => (advanced.value ? allColumns : allColumns.filter(column => column.key !== 'scopes')))

/**
 * #410: the catalog names what a definition will reach for *before* Clone is clicked, so nobody
 * binds publish by accident -- the same grouped-by-vendor read TaskDetailView.vue does for a
 * cloned task (@/lib/toolChips).
 */
function toolChips(task: CatalogTask): Array<{ vendor: string; methods: string[] }> {
    return groupToolsByVendor(task.currentVersion?.tools)
}

/** Gates read as `owner · approval` badges: who signs off, and how specific that sign-off is. */
function gateBadges(task: CatalogTask): Array<{ key: string; label: string; title: string }> {
    return buildGateBadges(task.currentVersion?.gates).map(badge => ({
        key: badge.key,
        label: `${badge.owner} · ${badge.approval}`,
        title: `${badge.tool} waits for ${badge.owner} (${badge.approval} approval)`,
    }))
}

/** The provenance column reads "Zero Human" for a Zero Human task. The stored value stays `zerohuman-crafted`. */
function provenanceLabel(provenance: string): string {
    return provenance === 'zerohuman-crafted' ? 'Zero Human' : provenance
}

/** This enterprise owns the definition, so Sync (catching a clone up to someone else's task) does not apply. */
function ownsEntry(task: CatalogTask): boolean {
    return task.ownedByMe === true
}

async function withBusyState<T>(taskId: string, action: () => Promise<T>): Promise<T | null> {
    busy.value = taskId
    error.value = null
    try {
        return await action()
    } finally {
        busy.value = null
    }
}

async function deprecate(task: CatalogTask): Promise<void> {
    if (!task.canEditDefinition || busy.value || cloning.value || copyBusy.value) return
    try {
        await withBusyState(task.id, async () => {
            await api.deprecateCatalogTask(task.id)
        })
        await load()
    } catch (caught) {
        error.value = caught instanceof Error ? `Could not deprecate (${caught.message}).` : 'Could not deprecate.'
    }
}

/** The entry whose star is being toggled, so a double click cannot send star and unstar at once. */
const starBusy = ref<string | null>(null)

/**
 * Stars or unstars an entry for this enterprise (#413), then shows the counts the API answers with rather than
 * guessing them, since another enterprise may have starred it meanwhile.
 */
async function toggleStar(task: CatalogTask): Promise<void> {
    if (starBusy.value) return
    starBusy.value = task.id
    error.value = null
    try {
        const updated = task.starredByMe ? await api.unstarCatalogTask(task.id) : await api.starCatalogTask(task.id)
        tasks.value = tasks.value.map(row =>
            row.id === task.id
                ? { ...row, starCount: updated.starCount ?? 0, starredByMe: updated.starredByMe ?? false }
                : row,
        )
    } catch (caught) {
        error.value =
            caught instanceof Error ? `Could not update the star (${caught.message}).` : 'Could not update the star.'
    } finally {
        starBusy.value = null
    }
}

/**
 * #1140: an entry this business already holds a linked clone of reads as a disabled "Cloned" button. The server
 * still refuses a second clone as the backstop for races; this is the feedback, not the enforcement.
 */
function isCloned(task: CatalogTask): boolean {
    return task.clonedByMe === true
}

/**
 * #1139: the Clone button reports its own progress and result in place, per row:
 * idle -> loading -> (success | error) -> idle again after CLONE_RESULT_MS.
 * `data-clone-state` on the button exposes the state, so tests do not depend on colour classes.
 */
type CloneState = 'idle' | 'loading' | 'success' | 'error'

const CLONE_RESULT_MS = 3000

type CloneLook = { label: string; icon: Component; variant: ButtonVariants['variant'] }

/**
 * The label, glyph and kit Button variant for each state. The result variants are the brand status colours
 * (BG §18.4): `success` means it finished well, `failure` that it ended badly. A sync reports its result on the
 * same two variants, so "ended well" and "ended badly" each look one way on this screen.
 */
const cloneLooks: Record<CloneState, CloneLook> = {
    idle: { label: 'Clone', icon: Copy, variant: 'default' },
    loading: { label: 'Cloning…', icon: LoaderCircle, variant: 'default' },
    success: { label: 'Cloned', icon: Check, variant: 'success' },
    error: { label: 'Error', icon: TriangleAlert, variant: 'failure' },
}

/** What each row's Clone button shows, keyed by task id. A row that is not listed is idle. */
const cloneStates = reactive(new Map<string, CloneState>())
const cloneSettleTimers = new Map<string, ReturnType<typeof setTimeout>>()

function cloneStateOf(task: CatalogTask): CloneState {
    return cloneStates.get(task.id) ?? 'idle'
}

function cloneLook(task: CatalogTask): CloneLook {
    const state = cloneStateOf(task)
    // #1140: once no result is showing, an entry this business holds rests on a disabled "Cloned".
    if (state === 'idle' && isCloned(task)) return { ...cloneLooks.idle, label: 'Cloned' }
    return cloneLooks[state]
}

/** Shows the result on the clicked row only, then returns that row to idle (Clone, or Cloned if now held). */
function showCloneResult(taskId: string, result: 'success' | 'error'): void {
    cloneStates.set(taskId, result)
    cloneSettleTimers.set(
        taskId,
        setTimeout(() => {
            cloneSettleTimers.delete(taskId)
            cloneStates.delete(taskId)
        }, CLONE_RESULT_MS),
    )
}

onBeforeUnmount(() => {
    for (const timer of cloneSettleTimers.values()) clearTimeout(timer)
    cloneSettleTimers.clear()
})

async function clone(task: CatalogTask): Promise<void> {
    if (isCloned(task) || cloning.value || busy.value || copyBusy.value || cloneStateOf(task) !== 'idle') return
    cloning.value = task.id
    cloneStates.set(task.id, 'loading')
    error.value = null
    try {
        // A clone names no team: a task belongs to a role, and every team where a member holds it runs it.
        const made = await api.cloneCatalogTask(task.id)
        // #1140: mark the row held in place, as the star toggle does, so it settles on "Cloned" without a reload.
        // #1260: and say which version the new clone is on straight away, as a reload would.
        tasks.value = tasks.value.map(row =>
            row.id === task.id ? { ...row, clonedByMe: true, cloneOnVersion: freshCloneVersion(row, made) } : row,
        )
        showCloneResult(task.id, 'success')
    } catch (caught) {
        // The button only says "Error". The reason stays in the page-level message.
        const code = caught instanceof Error ? caught.message : ''
        if (isCraftedRefusal(caught)) error.value = CRAFTED_NOT_IN_PLAN_MESSAGE
        else
            error.value =
                code === 'already_cloned'
                    ? `${messageName(task)} is already in your company.`
                    : `Could not clone (${code}).`
        showCloneResult(task.id, 'error')
    } finally {
        cloning.value = null
    }
}

/** How long a sync's outcome stays on the button before the row settles into its resting state. */
const SYNC_FEEDBACK_MS = 3000

/**
 * A row's one primary action. At rest it follows what the API says this enterprise holds: nothing (Clone), a
 * clone with the source's current version (a disabled Cloned), or a clone the source has moved past (an amber
 * Sync, never offered on a deprecated source, which the API refuses to sync). While a sync is in flight, and for
 * SYNC_FEEDBACK_MS after it settles, the sync's own state shows instead.
 */
type PrimaryAction = 'clone' | 'cloned' | 'sync' | SyncFeedback
type SyncFeedback = 'syncing' | 'synced' | 'sync-failed'

/** Entry id -> the sync in flight on it, or the outcome still showing. */
const syncFeedback = ref(new Map<string, SyncFeedback>())
const syncFeedbackTimers = new Map<string, ReturnType<typeof setTimeout>>()

function primaryAction(task: CatalogTask): PrimaryAction {
    // The owner already has the definition. A clone of their own task does not offer Sync, even when it is behind.
    if (ownsEntry(task) && cloneStateOf(task) === 'idle') return task.clonedByMe ? 'cloned' : 'clone'
    const feedback = syncFeedback.value.get(task.id)
    if (feedback) return feedback
    // #1139/#1140: a clone in flight, or its result still showing, stays on the Clone button that reports it,
    // even though a successful clone has already marked the row held.
    if (cloneStateOf(task) !== 'idle') return 'clone'
    if (!task.clonedByMe) return 'clone'
    return task.cloneStale && task.status !== 'deprecated' ? 'sync' : 'cloned'
}

/** Hint on the resting Cloned button. The owner is not waiting on a newer version of their own task. */
function clonedTitle(task: CatalogTask): string {
    return ownsEntry(task) ? 'Your enterprise owns this task.' : "Your clone has the source's current version."
}

/**
 * The entry's version a Sync would move this enterprise's clone onto (#1260): its own published version, while the
 * clone is behind it. Null when the clone is current, or the API sent no version number.
 */
function availableVersion(task: CatalogTask): number | null {
    return task.clonedByMe && task.cloneStale ? entryOwnerVersion(task.currentVersion) : null
}

/**
 * Whose task this enterprise's clone is, the entry's version it is on, and the one available, as the clone's task
 * page reads it (#1260): "Zero Human's eng-implement-issue, version 7 · on 7, 8 available", then
 * "Zero Human's eng-implement-issue, version 8" once current. With no owner name, no possessive.
 */
function cloneVersionLabel(task: CatalogTask): string | null {
    if (ownsEntry(task) || !task.clonedByMe || task.cloneOnVersion == null) return null
    const named = ownerVersionLabel(task.ownerName, messageName(task), task.cloneOnVersion)
    const available = availableVersion(task)
    return available === null ? named : `${named} · ${cloneOnLabel(task.cloneOnVersion, available)}`
}

/**
 * The entry's version a clone just made is on (#1260): the entry's current version, when the new clone's pin names
 * the version this page holds. Null when the source published again since the page loaded, so the row never shows a
 * number the clone is not on; the next load reads it from the server.
 */
function freshCloneVersion(
    task: CatalogTask,
    made: Pick<TaskRow, 'clonedFromVersionId'> | null | undefined,
): number | null {
    const pinned = made?.clonedFromVersionId
    return pinned && pinned === task.currentVersionId ? entryOwnerVersion(task.currentVersion) : null
}

/** Shows `feedback` on `taskId`'s button; an outcome clears itself after SYNC_FEEDBACK_MS. `null` clears it now. */
function showSyncFeedback(taskId: string, feedback: SyncFeedback | null): void {
    clearTimeout(syncFeedbackTimers.get(taskId))
    syncFeedbackTimers.delete(taskId)
    if (!feedback) {
        syncFeedback.value.delete(taskId)
        return
    }
    syncFeedback.value.set(taskId, feedback)
    if (feedback !== 'syncing') {
        syncFeedbackTimers.set(
            taskId,
            setTimeout(() => showSyncFeedback(taskId, null), SYNC_FEEDBACK_MS),
        )
    }
}

/**
 * Replaces `task`'s row with what the API now reports for it, so its clone state is the server's, never a guess:
 * an enterprise that still holds another stale clone of the entry, or whose source published again since the page
 * loaded, still reads as stale and keeps Sync. Re-reads the page the table is showing and swaps in that one row. A
 * failed read, or a row no longer on the page, leaves the row as it was rather than marking it current.
 */
async function refreshRow(task: CatalogTask): Promise<void> {
    const query = listQuery()
    try {
        const fresh = asListResponse(await api.catalogTasks(query), query).data.find(row => row.id === task.id)
        if (fresh) tasks.value = tasks.value.map(row => (row.id === task.id ? fresh : row))
    } catch {
        error.value = `Synced ${messageName(task)}, but could not refresh the catalog. Reload to see its current state.`
    }
}

/**
 * Brings this enterprise's clone of `task` up to date. There is one clone per enterprise, so the team picker plays
 * no part. On success the row is re-read from the API while green "Cloned" shows, so it settles on whatever the
 * server now reports; on failure it is still stale, so Sync comes back for a retry once the error has shown.
 */
async function sync(task: CatalogTask): Promise<void> {
    if (syncFeedback.value.has(task.id) || busy.value || cloning.value || copyBusy.value) return
    showSyncFeedback(task.id, 'syncing')
    try {
        await withBusyState(task.id, () => api.syncCatalogTask(task.id))
    } catch (caught) {
        if (isCraftedRefusal(caught)) error.value = CRAFTED_NOT_IN_PLAN_MESSAGE
        else
            error.value =
                caught instanceof Error
                    ? `Could not sync ${messageName(task)} (${caught.message}).`
                    : `Could not sync ${messageName(task)}.`
        showSyncFeedback(task.id, 'sync-failed')
        return
    }
    showSyncFeedback(task.id, 'synced')
    await refreshRow(task)
}

onBeforeUnmount(() => {
    for (const timer of syncFeedbackTimers.values()) clearTimeout(timer)
    syncFeedbackTimers.clear()
})
</script>

<template>
    <section class="w-full space-y-6 p-4 sm:p-8">
        <header class="space-y-2">
            <ZhPageTitle>Catalog</ZhPageTitle>
            <p class="text-muted-foreground text-sm">Find the team, teammate or task for your next piece of work.</p>
        </header>
        <ZhPanel class="space-y-4" aria-label="Find a template">
            <form class="flex gap-2" aria-label="Search Catalog" @submit.prevent="submitSearch">
                <ListSearchField v-model="searchDraft" class="min-w-0 flex-1" aria-label="Search Catalog" placeholder="Search templates" />
                <Button type="submit">Search</Button>
            </form>
            <nav class="flex flex-wrap gap-2" aria-label="Template type">
                <Button v-for="item in catalogKinds" :key="item.value" :variant="kind === item.value ? 'default' : 'ghost'" :aria-pressed="kind === item.value" @click="kind = item.value">{{ item.label }}</Button>
            </nav>
            <p class="text-muted-foreground text-sm">{{ currentKind.hint }}</p>
            <!-- #1727: both filters apply to Teams, Roles and Tasks alike, and stay set when the type changes. -->
            <div class="flex flex-wrap items-end gap-4">
                <div class="grid min-w-48 gap-1 text-sm">
                    <span id="catalog-business-function" class="text-muted-foreground">Business function</span>
                    <Select :model-value="businessFunction" aria-label="Business function" @update:model-value="chooseFunction">
                        <SelectTrigger class="w-full" aria-labelledby="catalog-business-function">
                            <SelectValue placeholder="All functions" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem :value="ALL_FUNCTIONS">All functions</SelectItem>
                            <SelectItem v-for="option in BUSINESS_FUNCTION_OPTIONS" :key="option.value" :value="option.value">{{ option.label }}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <label class="flex items-center gap-2 text-sm">
                    <Checkbox :model-value="needsNoTools" @update:model-value="chooseNeedsNoTools" />
                    Needs no tools
                </label>
                <Button v-if="!list.isDefault.value" type="button" variant="outline" size="sm" @click="clearFilters">Clear filters</Button>
            </div>
        </ZhPanel>
        <p v-if="error && kind === 'task'" role="alert" class="text-destructive text-sm">{{ error }}</p>
        <RouterLink v-if="planRefused && kind === 'task'" to="/billing#plans">See plans on Billing</RouterLink>
        <KeepAlive>
            <CatalogAdoptionPanel v-if="kind !== 'task'" :key="kind" :kind="kind" :teams="teams" :search-query="search" :filter-query="filterQuery" @adopted="adopted" @team-created="teamCreated" />
        </KeepAlive>
        <ZhPanel v-if="kind === 'task'" class="space-y-4" aria-labelledby="catalog-tasks-heading">
			<ZhSectionHeading id="catalog-tasks-heading">Tasks</ZhSectionHeading>
            <p class="text-muted-foreground text-sm">Clone keeps a link to the original. Copy makes an editable task you own.</p>

			<p class="text-muted-foreground text-sm" data-testid="catalog-clone-role-note">A clone or copy runs as the template's role: whoever holds the role runs it, and every team they sit on lists it.</p>

			<TaskCopyForm v-if="copySource" :key="copySource.id" :source="copySource" :roles="roles" :create-copy="createCopy" :disabled="Boolean(busy) || Boolean(cloning)" @copied="copied" @cancel="copySource = null" @busy-change="copyBusy = $event" />

		<!--
			storage-key bumped for #410: a column list saved before Scopes existed would keep hiding it,
			and the whole point of the column is that nobody clones without first reading the scope. Bumped
			again for #1727: the Task column is keyed by name now, and a list saved with the slug key would hide it.
			Keyed by mode, so switching to Advanced draws the Scopes column straight away. Each mode also saves its
			own column list: DataTable keeps the visible keys and hides any column missing from them, and a list
			saved in Standard (which has no Scopes column) would otherwise hide Scopes in Advanced.
		-->
		<DataTable
			:key="mode"
			:storage-key="advanced ? 'catalog-names-advanced' : 'catalog-names'"
			:columns="columns"
            hide-search-input
            stacked-on-mobile
			:rows="tasks as unknown as Record<string, unknown>[]"
			:total="total"
			:page="page"
			:limit="limit"
			:pages="pages"
			:loading="loading"
			:search="search"
			:sort="sort"
			:order="order"
			:filters="filters"
			@update:page="page = $event"
			@update:limit="limit = $event"
			@update:search="search = $event"
			@update:sort="sort = $event"
			@update:order="order = $event"
			@update:filters="filters = $event"
		>
			<!-- Social proof (#413): how many enterprises cloned and starred this entry. Zero reads as 0, never a blank. -->
			<template #cell-name="{ row }">
				<div class="font-medium">{{ taskDisplayName(row as CatalogTask) }}</div>
				<!-- #1727: the slug is Advanced-only, muted under the name. -->
				<div v-if="advanced" class="text-muted-foreground font-mono text-xs" data-testid="task-slug">{{ (row as CatalogTask).slug }}</div>
				<div class="text-muted-foreground mt-1 flex flex-wrap items-center gap-3 text-xs">
					<span class="inline-flex items-center gap-1">
						<Copy class="size-3" aria-hidden="true" />
						<span data-testid="clone-count">{{ (row as CatalogTask).cloneCount ?? 0 }}</span>
						clones
					</span>
					<Button
						type="button"
						size="xs"
						variant="ghost"
						data-testid="star-toggle"
						:aria-pressed="(row as CatalogTask).starredByMe === true"
						:aria-label="(row as CatalogTask).starredByMe ? `Unstar ${taskDisplayName(row as CatalogTask)}` : `Star ${taskDisplayName(row as CatalogTask)}`"
						:title="(row as CatalogTask).starredByMe ? 'Starred by your enterprise. Click to unstar.' : 'Star this task'"
						:disabled="Boolean(starBusy)"
						@click.stop="toggleStar(row as CatalogTask)"
					>
						<Star class="size-3" :class="(row as CatalogTask).starredByMe ? 'fill-current text-warning-amber' : ''" aria-hidden="true" />
						<span data-testid="star-count">{{ (row as CatalogTask).starCount ?? 0 }}</span>
						stars
					</Button>
				</div>
			</template>

			<template #cell-currentVersion.goal="{ row }">
				<!-- #1727: one line, the goal's first sentence; the full goal is the title, and in Details. -->
				<div
					v-if="(row as CatalogTask).currentVersion?.goal"
					class="text-muted-foreground max-w-md truncate text-sm"
					:title="(row as CatalogTask).currentVersion?.goal"
				>{{ firstSentence((row as CatalogTask).currentVersion?.goal) }}</div>
				<div v-else class="text-muted-foreground text-sm">—</div>
			</template>

			<!-- Absence of a scope is a fact worth stating, so an empty list reads as "none declared", never as a blank cell. -->
			<template #cell-scopes="{ row }">
				<!-- A tool is metadata (a system label); a gate is a human sign-off, so it takes the action-required pill. -->
				<div class="space-y-1 text-xs">
					<div v-if="toolChips(row as CatalogTask).length" class="flex flex-wrap items-center gap-x-2 gap-y-1">
						<span class="text-muted-foreground">Tools</span>
						<ZhSystemLabel v-for="chip in toolChips(row as CatalogTask)" :key="chip.vendor" as="span" :title="chip.methods.join(', ')">{{ chip.vendor }}</ZhSystemLabel>
					</div>
					<ZhEmptyState v-else compact label="Tools">No tools declared</ZhEmptyState>
					<div v-if="gateBadges(row as CatalogTask).length" class="flex flex-wrap items-center gap-x-2 gap-y-1">
						<span class="text-muted-foreground">Gates</span>
						<ZhStatusPill v-for="badge in gateBadges(row as CatalogTask)" :key="badge.key" tone="action" :pulse="false" :title="badge.title">{{ badge.label }}</ZhStatusPill>
					</div>
					<ZhEmptyState v-else compact label="Gates">No gates declared</ZhEmptyState>
				</div>
			</template>

			<template #cell-provenance="{ row }">
				<span data-testid="provenance">{{ provenanceLabel((row as CatalogTask).provenance) }}</span>
			</template>

			<template #cell-status="{ row }">
				<span :class="(row as CatalogTask).status === 'deprecated' ? 'text-muted-foreground' : 'text-foreground'">
					{{ (row as CatalogTask).status || 'active' }}
				</span>
			</template>

			<template #cell-actions="{ row }">
				<div class="flex justify-end gap-2">
					<!-- #1260: whose task the clone is, the version of this entry it is on, and the one available, in the entry's numbering. -->
					<span v-if="cloneVersionLabel(row as CatalogTask)" data-testid="clone-version" class="text-muted-foreground max-w-64 self-center text-right text-xs">{{ cloneVersionLabel(row as CatalogTask) }}</span>
					<!--
						#1139: loading while in flight, then "Cloned" (success) or "Error" (failure) for 3s, then Clone again.
						#1140: an entry this business holds (on load, or after that success) rests on a disabled "Cloned" instead.
						A sync reports on the same success and failure variants, and is in motion (warning) until then.
					-->
					<Button
						v-if="primaryAction(row as CatalogTask) === 'clone'"
						type="button"
						size="sm"
						:variant="cloneLook(row as CatalogTask).variant"
						:data-clone-state="cloneStateOf(row as CatalogTask)"
						:aria-busy="cloneStateOf(row as CatalogTask) === 'loading'"
						:disabled="isCloned(row as CatalogTask) || Boolean(busy) || copyBusy || (row as CatalogTask).status === 'deprecated' || cloneStateOf(row as CatalogTask) !== 'idle'"
						:title="isCloned(row as CatalogTask) ? 'Your business already cloned this task.' : undefined"
						@click="clone(row as CatalogTask)"
					>
						<component
							:is="cloneLook(row as CatalogTask).icon"
							class="mr-1 size-4"
							:class="{ 'motion-safe:animate-spin': cloneStateOf(row as CatalogTask) === 'loading' }"
							aria-hidden="true"
						/>
						{{ cloneLook(row as CatalogTask).label }}
					</Button>
					<Button v-else-if="primaryAction(row as CatalogTask) === 'cloned'" type="button" size="sm" variant="outline" :data-clone-state="cloneStateOf(row as CatalogTask)" disabled :title="clonedTitle(row as CatalogTask)">
						<Check class="mr-1 size-4" aria-hidden="true" />
						Cloned
					</Button>
					<!-- The source has published a newer version than this enterprise's clone. -->
					<Button
						v-else-if="primaryAction(row as CatalogTask) === 'sync'"
						type="button"
						size="sm"
						variant="warning"
						title="The source has a newer version. Sync makes it your clone's current version. Earlier versions, including any local edits, stay in the clone's history."
						:disabled="Boolean(busy) || copyBusy"
						@click="sync(row as CatalogTask)"
					>
						<RefreshCw class="mr-1 size-4" aria-hidden="true" />
						Sync<template v-if="availableVersion(row as CatalogTask) !== null"> to {{ availableVersion(row as CatalogTask) }}</template>
					</Button>
					<Button v-else-if="primaryAction(row as CatalogTask) === 'syncing'" type="button" size="sm" variant="warning" disabled aria-busy="true">
						<LoaderCircle class="mr-1 size-4 animate-spin" aria-hidden="true" />
						Syncing…
					</Button>
					<Button v-else-if="primaryAction(row as CatalogTask) === 'synced'" type="button" size="sm" variant="success" disabled>
						<Check class="mr-1 size-4" aria-hidden="true" />
						Cloned
					</Button>
					<Button v-else type="button" size="sm" variant="failure" disabled>
						<TriangleAlert class="mr-1 size-4" aria-hidden="true" />
						Error
					</Button>
					<Button type="button" size="sm" variant="outline" :disabled="Boolean(busy) || copyBusy || (row as CatalogTask).status === 'deprecated' || (row as CatalogTask).origin === 'os'" @click="copySource = row as CatalogTask"><Files class="mr-1 size-4" />Copy</Button>
					<Button type="button" size="sm" variant="ghost" @click="detailsTask = row as CatalogTask">Details</Button>
					<Button
						v-if="(row as CatalogTask).status !== 'deprecated' && (row as CatalogTask).canEditDefinition"
						type="button"
						size="sm"
						variant="outline"
						:disabled="Boolean(busy) || copyBusy"
						@click="deprecate(row as CatalogTask)"
					>
						<Trash2 class="mr-1 size-4" />
						Deprecate
					</Button>
				</div>
			</template>
		</DataTable>

		<!--
			#1727: a task's details, one click from its row. Standard names each tool by its vendor only and never shows
			the slug; Advanced adds the slug and the exact tool names under each vendor.
		-->
		<Sheet :open="detailsTask !== null" @update:open="closeDetails">
			<SheetContent v-if="detailsTask" class="overflow-y-auto" data-testid="catalog-task-details">
				<SheetHeader>
					<SheetTitle>{{ taskDisplayName(detailsTask) }}</SheetTitle>
					<p class="text-muted-foreground text-xs">
						<template v-if="entryOwnerVersion(detailsTask.currentVersion) !== null">Version {{ entryOwnerVersion(detailsTask.currentVersion) }} · </template>{{ provenanceLabel(detailsTask.provenance) }}
					</p>
					<SheetDescription>{{ detailsTask.currentVersion?.goal || 'No goal written.' }}</SheetDescription>
				</SheetHeader>
				<dl class="px-4 pb-4">
					<ZhKeyValue v-if="advanced" label="Slug">{{ detailsTask.slug }}</ZhKeyValue>
					<ZhKeyValue label="Tools">
						<div v-for="chip in toolChips(detailsTask)" :key="chip.vendor" class="flex flex-wrap items-center gap-2">
							<ZhSystemLabel as="span">{{ chip.vendor }}</ZhSystemLabel>
							<span v-if="advanced">{{ chip.methods.join(', ') }}</span>
						</div>
						<template v-if="!toolChips(detailsTask).length">No tools needed</template>
					</ZhKeyValue>
					<ZhKeyValue label="Approvals">{{ gateBadges(detailsTask).map(badge => badge.label).join(', ') || 'No approval gates' }}</ZhKeyValue>
				</dl>
			</SheetContent>
		</Sheet>
		</ZhPanel>
	</section>
</template>
